/**
 * AuditLogRow Component
 * Individual audit log row with expandable details for projection edits
 */

import React, { useState } from 'react';
import { AuditLogEntry, AuditChange } from '../../types/audit';
import { formatAuditAction, formatFieldName, formatProjectionChangeValue } from '../../services/auditApi';

interface AuditLogRowProps {
  entry: AuditLogEntry;
}

/**
 * Format timestamp for display
 */
function formatTimestamp(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * Get action badge color
 */
function getActionBadgeClass(action: string): string {
  switch (action) {
    case 'LOGIN':
      return 'bg-green-100 text-green-800';
    case 'LOGOUT':
      return 'bg-gray-100 text-gray-800';
    case 'ACCESS_DENIED':
      return 'bg-red-100 text-red-800';
    case 'PROJECTION_EDIT':
      return 'bg-blue-100 text-blue-800';
    case 'PROJECTION_MONTHLY_ADJUSTMENT':
      return 'bg-indigo-100 text-indigo-800';
    case 'AGENT_KEY_CREATE':
    case 'AGENT_KEY_REDEEM':
      return 'bg-purple-100 text-purple-800';
    case 'AGENT_KEY_REVOKE':
      return 'bg-orange-100 text-orange-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

export const AuditLogRow: React.FC<AuditLogRowProps> = ({ entry }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Projection edits (annual inputs) and executive monthly adjustments (one change per month)
  const isProjectionAction = entry.action === 'PROJECTION_EDIT' || entry.action === 'PROJECTION_MONTHLY_ADJUSTMENT';
  const changes = entry.details?.changes || [];
  const droppedAdjustments = entry.details?.droppedAdjustments || [];
  // A save whose only effect was clearing executive adjustments has no field changes
  const hasDetails = isProjectionAction && (changes.length > 0 || droppedAdjustments.length > 0);

  return (
    <>
      {/* Main Row */}
      <tr
        className={`border-b border-gray-200 ${hasDetails ? 'cursor-pointer hover:bg-gray-50' : ''}`}
        onClick={() => hasDetails && setIsExpanded(!isExpanded)}
      >
        {/* Timestamp */}
        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
          {formatTimestamp(entry.timestamp)}
        </td>

        {/* User */}
        <td className="px-4 py-3 text-sm">
          <div className="font-medium text-gray-900">{entry.userDisplayName}</div>
          <div className="text-xs text-gray-500">{entry.userEmail}</div>
        </td>

        {/* Action */}
        <td className="px-4 py-3">
          <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getActionBadgeClass(entry.action)}`}>
            {formatAuditAction(entry.action)}
          </span>
        </td>

        {/* Details/Location */}
        <td className="px-4 py-3 text-sm text-gray-600">
          {isProjectionAction && (entry.locationName || hasDetails) ? (
            <div className="flex items-center">
              {hasDetails && (
                <span className="mr-2 text-gray-400">
                  {isExpanded ? '▼' : '▶'}
                </span>
              )}
              <span>{entry.locationName || (entry.locationId !== undefined ? `Location ${entry.locationId}` : '')}</span>
              {changes.length > 0 && (
                <span className="ml-2 text-xs text-gray-400">
                  ({changes.length} change{changes.length !== 1 ? 's' : ''})
                </span>
              )}
              {droppedAdjustments.length > 0 && (
                <span className="ml-2 text-xs text-amber-600">
                  ({droppedAdjustments.length} adjustment{droppedAdjustments.length !== 1 ? 's' : ''} cleared)
                </span>
              )}
              {entry.details?.reset && <span className="ml-2 text-xs text-gray-400">(reset)</span>}
            </div>
          ) : (
            <span className="text-gray-400">-</span>
          )}
        </td>

        {/* IP Address */}
        <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
          {entry.details?.ipAddress || '-'}
        </td>
      </tr>

      {/* Expanded Details */}
      {isExpanded && hasDetails && (
        <tr className="bg-blue-50">
          <td colSpan={5} className="px-4 py-3">
            <div className="pl-8">
              {droppedAdjustments.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs font-medium text-amber-700 mb-1">Executive monthly adjustments cleared by this save:</div>
                  <div className="space-y-1">
                    {droppedAdjustments.map((d, idx) => (
                      <div key={idx} className="text-sm text-gray-700">
                        {formatFieldName(`${d.department}#${d.metric}`)} (by {d.adjustedByName}): {d.reason}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {changes.length > 0 && <div className="text-xs font-medium text-gray-500 mb-2">Changes:</div>}
              <div className="space-y-1">
                {changes.map((change: AuditChange, idx: number) => (
                  <div key={idx} className="text-sm flex items-center">
                    <span className="font-medium text-gray-700 mr-2">
                      {formatFieldName(change.field)}:
                    </span>
                    <span className="text-red-600 line-through mr-2">
                      {formatProjectionChangeValue(change.field, change.oldValue)}
                    </span>
                    <span className="text-gray-400 mr-2">→</span>
                    <span className="text-green-600 font-medium">
                      {formatProjectionChangeValue(change.field, change.newValue)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};
